import React, { useContext } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { AuthContext } from "../context/AuthContext";
import { usePermissions } from "../hooks/usePermissions";
import { ShieldX, ArrowLeft, LayoutDashboard } from "lucide-react";

interface RoleProtectedRouteProps {
  children: React.ReactNode;
  allowed: string[];
  module?: string;
  /** Which permission bit to require on `module` — defaults to "any" (Read/Write/Delete) for
   *  view-style routes. Use "Write" for add/edit forms, "Delete" for delete-only actions. */
  action?: "Read" | "Write" | "Delete";
}

const ROLE_LABELS: Record<string, string> = {
  TENANT_ADMIN:          "Tenant Admin",
  SOCIETY_MANAGER:       "Society Manager",
  ACCOUNTANT:            "Accountant",
  AUDITOR:               "Auditor",
  STAFF:                 "Staff",
  COMMITTEE_MEMBER:      "Committee Member",
  RESIDENT:              "Resident",
  VENDOR_REPRESENTATIVE: "Vendor Representative",
  SUPER_ADMIN:           "Platform Admin",
};

const AccessDenied: React.FC<{ role: string }> = ({ role }) => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-gray-50 flex items-center justify-center p-6">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-10 max-w-md w-full text-center space-y-6">

        {/* Icon */}
        <div className="flex justify-center">
          <div className="w-16 h-16 rounded-full bg-red-50 flex items-center justify-center">
            <ShieldX className="text-red-500" size={30} />
          </div>
        </div>

        {/* Copy */}
        <div className="space-y-2">
          <h1 className="text-xl font-bold text-gray-800">Access Restricted</h1>
          <p className="text-sm text-gray-500">
            You don't have permission to access this module.
          </p>
          <p className="text-sm text-gray-400">
            If you believe this is a mistake, please contact your Tenant Administrator.
          </p>
        </div>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 border border-gray-300 text-gray-700 text-sm font-semibold rounded-xl hover:bg-gray-50 transition-colors"
          >
            <ArrowLeft size={15} />
            Go Back
          </button>
          <button
            onClick={() => navigate("/dashboard")}
            className="flex-1 flex items-center justify-center gap-2 py-2.5 bg-blue-600 text-white text-sm font-semibold rounded-xl hover:bg-blue-700 transition-colors"
          >
            <LayoutDashboard size={15} />
            Dashboard
          </button>
        </div>

        {/* Role badge */}
        <p className="text-xs text-gray-400">
          Logged in as: <span className="font-medium">{ROLE_LABELS[role] ?? role}</span>
        </p>
      </div>
    </div>
  );
};

const RoleProtectedRoute: React.FC<RoleProtectedRouteProps> = ({ children, allowed, module, action }) => {
  const { user } = useContext(AuthContext);
  const { isAdmin, canRead, canWrite, canDelete } = usePermissions();

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const role = user.effectiveRole || user.role;

  if (isAdmin) {
    return <>{children}</>;
  }

  if (module) {
    const hasPerm =
      action === "Write" ? canWrite(module) :
      action === "Delete" ? canDelete(module) :
      canRead(module);
    if (hasPerm) return <>{children}</>;
  }

  if (allowed.includes(role)) {
    return <>{children}</>;
  }

  return <AccessDenied role={role} />;
};

export default RoleProtectedRoute;
